import { Link, Navigate, useSearchParams } from "react-router";
import { paths } from "@shared-core/routing/routes";
import { Card } from "../../../app/components/primitives/card";
import { Button } from "../../../app/components/primitives/button";
import { PortalSignInShell } from "./PortalSignInShell";
import { PortalReviewBar } from "./PortalReviewBar";
import { PortalSignInOutcomeGate } from "./PortalSignInOutcomeGate";
import { outcomePath } from "./outcomePath";

// A customer the person works as is blocked by staff (a legal or credit stop).
// The customer is told, by name, that it is unavailable and who to contact, and
// nothing more: the reason is for staff and never leaves the building (Brad's
// decision, Q16: a per-customer block, audited and reversible; Decision 3: the
// portal says who is blocked, never why).
//
// Two ways in. Every customer the person is linked to is blocked: the page
// names them all. Or the person has other customers that work but the one they
// were using is blocked (mid-session, or remembered from an earlier visit): the
// page names that one, carried as `?customer=<id>` (an id, so a refresh shows
// the same page), and offers the chooser. The portal never switches them to
// another customer silently (28_ section 5.2, Brad, 14 Oct 2026). The chooser
// shows a blocked customer as one disabled row (PortalChooseCustomerPage).
//
// Users have no names (docs/features/portal-sign-in/06, 5.1), so neither page greets anyone. After a sign-in the page says
// the portal is on hold and offers *Use another address* (`refused.customer-hold`); on an emailed link nobody is signed
// in, so the page offers nothing to click (`link.acting-link.customer-hold`).

const ACCOUNT_MANAGER_PHONE = "01752 663111";

/** An emailed link to a customer whose portal is on hold: the link can't be used, and there is no one to sign in again. */
export function LinkCustomerHoldPage({
  customerName,
}: {
  customerName: string;
}) {
  return (
    <PortalSignInShell
      eyebrow={customerName}
      title="This link can't be used right now"
      subtitle={`${customerName}'s use of the portal is on hold.`}
    >
      <Card className="gap-3 p-5">
        <p className="text-[13px] text-muted-foreground">
          Please contact your account manager at Pollards on{" "}
          {ACCOUNT_MANAGER_PHONE}.
        </p>
      </Card>
    </PortalSignInShell>
  );
}

/** "A", "A and B", "A, B and C". */
function listNames(names: string[]): string {
  if (names.length <= 1) return names[0] ?? "";
  return `${names.slice(0, -1).join(", ")} and ${names[names.length - 1]}`;
}

export function PortalCustomerBlockedPage() {
  const [searchParams] = useSearchParams();
  const pausedCustomerId = searchParams.get("customer");
  return (
    <PortalSignInOutcomeGate
      kinds={
        ["customer_blocked", "choose_customer", "welcome", "ready"] as const
      }
    >
      {(outcome, scenario) => {
        const paused = outcome.customers.find(
          (c) => c.customer_id === pausedCustomerId && c.is_blocked,
        );
        const hasOtherUsableCustomers = outcome.customers.some(
          (c) => !c.is_blocked,
        );
        // Not every customer blocked, and nothing paused to show (no id, a stale link, or the block has
        // since been lifted): this page has nothing to say, so the outcome's own page takes over.
        if (outcome.kind !== "customer_blocked" && !paused) {
          return <Navigate to={outcomePath(outcome, scenario)} replace />;
        }
        const heldNames =
          outcome.kind === "customer_blocked" || !paused
            ? outcome.customers.map((c) => c.customer_name)
            : [paused.customer_name];
        const names = listNames(heldNames);
        const several = heldNames.length > 1;
        return (
          <PortalSignInShell
            eyebrow={several ? undefined : names}
            title={
              several
                ? `The portals for ${names} are on hold`
                : `${names}'s portal is on hold`
            }
            footer={<PortalReviewBar current={scenario} />}
          >
            <Card className="gap-3 p-5">
              <p className="text-sm">
                {several
                  ? `Ordering and the portals are paused for ${names} while we sort out their accounts with us.`
                  : `Ordering and the portal are paused for ${names} while we sort out ${names}'s account with us.`}{" "}
                Please contact your account manager on {ACCOUNT_MANAGER_PHONE}.
              </p>
              {hasOtherUsableCustomers && (
                <Button asChild className="w-full">
                  <Link
                    to={paths.portalChooseCustomer({ scenario, change: true })}
                  >
                    Choose another customer
                  </Link>
                </Button>
              )}
              <Button asChild variant="outline" className="w-full">
                <Link to={paths.portalSignIn()}>Use another address</Link>
              </Button>
            </Card>
          </PortalSignInShell>
        );
      }}
    </PortalSignInOutcomeGate>
  );
}
